<template>
    <div class="box">
        <!-- Extraction limit reached warning -->
        <div v-if="store.isExtractionLimitReached" class="notification is-warning">
            <div class="is-flex is-align-items-center">
                <span class="icon mr-2">
                    <i class="fas fa-exclamation-triangle"></i>
                </span>
                <div>
                    <strong>{{ translate("page.category_manager.extraction_limit_reached_title") }}</strong>
                    <p>
                        {{ translate("page.category_manager.extraction_limit_reached_description", false, [MAX_EXTRACTIONS_PER_EVENT]) }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Remaining extractions info -->
        <div v-else class="notification is-info is-light mb-4">
            <div class="is-flex is-align-items-center">
                <span class="icon mr-2">
                    <i class="fas fa-info-circle"></i>
                </span>
                <div>
                    {{
                        translate("page.category_manager.extraction_remaining_info", false, [
                            store.remainingExtractions,
                            MAX_EXTRACTIONS_PER_EVENT,
                        ])
                    }}
                </div>
            </div>
        </div>

        <div v-if="!store.isExtractionLimitReached" class="mb-4">
            <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
                <label class="label mb-0">{{ translate("page.category_manager.extract_rules_textarea_label") }}</label>
                <span class="tag" :class="{ 'is-danger': store.isExtractionSourceTextOverLimit }">
                    {{ store.sourceText.length }} / {{ MAX_TEXT_CHARS }}
                </span>
            </div>
            <textarea
                v-model="store.sourceText"
                class="textarea"
                :placeholder="translate('page.category_manager.extract_rules_textarea_placeholder')"
                rows="15"
                :maxlength="MAX_TEXT_CHARS"
            ></textarea>
            <p class="help is-size-6">{{ translate("page.category_manager.extract_rules_textarea_help") }}</p>
        </div>
    </div>

    <hr />

    <div class="buttons is-flex is-justify-content-space-between">
        <button class="button" @click="store.navigateTo(null)">
            <span class="icon">
                <i class="fas fa-arrow-left"></i>
            </span>
            <span>{{ translate("page.category_manager.back_to_options") }}</span>
        </button>

        <button class="button is-success" :disabled="!canProceed" :class="{ 'is-loading': isProcessing }" @click="handleProceed">
            <span>{{ translate("page.category_manager.extract_rules_proceed_button") }}</span>
            <span v-if="!isProcessing" class="icon">
                <i class="fas fa-arrow-right"></i>
            </span>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import { translate } from "../../Common/translations";
    import { MAX_TEXT_CHARS, MAX_EXTRACTIONS_PER_EVENT, useCategoriesManagerStore } from "../categoriesManagerStore";
    import categoriesExtractionApi from "../../Api/Categories/categoriesExtraction";
    import { handleError } from "../../Common/errorHandler";

    const store = useCategoriesManagerStore();
    const isProcessing = ref(false);

    const canProceed = computed(
        () =>
            store.sourceText.trim().length > 0 &&
            !store.isExtractionSourceTextOverLimit &&
            !isProcessing.value &&
            !store.isExtractionLimitReached,
    );

    function handleProceed() {
        isProcessing.value = true;

        return categoriesExtractionApi
            .initiateExtraction({ eventId: store.eventId, sourceText: store.sourceText })
            .then(result => {
                store.currentExtractionId = result.id;
                return store.refreshExtractions();
            })
            .then(() => {
                store.showNewExtractionForm = false;
                store.sourceText = "";
            })
            .catch(handleError)
            .finally(() => {
                isProcessing.value = false;
            });
    }
</script>

<style scoped>
    .textarea {
        font-family: monospace;
        font-size: 0.9rem;
    }
</style>
